// src/components/StarsBackground/StarsBackground.tsx
import React, { useEffect, useRef } from 'react';
import './StarsBackground.css';

const StarsBackground: React.FC = () => {
  const starsRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = starsRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Stars array
    const stars: Array<{
      x: number;
      y: number;
      radius: number;
      opacity: number;
      twinkleSpeed: number;
    }> = [];

    // Create stars
    const createStars = () => {
      const numStars = Math.min(150, Math.floor((canvas.width * canvas.height) / 8000));
      stars.length = 0; // Clear existing stars
      
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 1.5,
          opacity: Math.random(),
          twinkleSpeed: Math.random() * 0.02 + 0.005,
        });
      }
    };

    createStars();

    // Animation
    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      stars.forEach((star) => {
        star.opacity += star.twinkleSpeed;
        if (star.opacity >= 1 || star.opacity <= 0) {
          star.twinkleSpeed = -star.twinkleSpeed;
        }
        star.opacity = Math.max(0, Math.min(1, star.opacity));

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup function with proper ref handling
    return () => {
      const currentCanvas = canvas; // Copy ref to variable for cleanup
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
      
      // Clear canvas if it still exists
      if (currentCanvas && currentCanvas.getContext) {
        const cleanupCtx = currentCanvas.getContext('2d');
        if (cleanupCtx) {
          cleanupCtx.clearRect(0, 0, currentCanvas.width, currentCanvas.height);
        }
      }
    };
  }, []); // Empty dependency array is correct here

  return (
    <canvas
      ref={starsRef}
      className="stars-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    />
  );
};

export default StarsBackground;